import { useState } from "react";

import type { LabelType } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Button,
  MenuSelect,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { CheckIcon } from "@braintrust/braintrust-ui-components/Icons";

import styles from "./style.module.scss";

export type CustomizeColumnsButtonProps<
  TValue extends string = string,
  TOption extends { label: string; value: string } = {
    label: string;
    value: TValue;
  },
> = {
  columnsOptions: TOption[];
  selectedColumns: NoInfer<TValue>[];
  onColumnsChange: (value: TValue[]) => void;
  onColumnsMenuClose?: () => void;
};

export const CustomizeColumnsButton = <TValue extends string>({
  columnsOptions,
  selectedColumns,
  onColumnsChange,
  onColumnsMenuClose,
}: CustomizeColumnsButtonProps<TValue>) => {
  const [isOpen, setIsOpen] = useState(false);

  const onOpen = () => {
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
    onColumnsMenuClose?.();
  };

  return (
    <MenuSelect
      id="customize-columns-menu"
      className={styles.select}
      open={isOpen}
      onClose={onClose}
      anchor={
        <Button
          variant="no-padding"
          disableRipple
          aria-controls="customize-columns-menu"
          aria-haspopup="true"
          onClick={onOpen}
        >
          <Typography
            component="span"
            style={{ textDecoration: "underline" }}
            fontWeight={isOpen ? 500 : 400}
            color="grey-1"
            sx={{ minWidth: "149px" }}
          >
            Customize columns
          </Typography>
        </Button>
      }
      options={columnsOptions}
      value={selectedColumns}
      multiple
      renderCustomItemLabel={({ selected, optionLabel }) => (
        <ItemLabel selected={selected} optionLabel={optionLabel} />
      )}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      onChange={onColumnsChange}
    />
  );
};

type ItemLabelProps = {
  selected: boolean | undefined;
  optionLabel: LabelType;
};

const ItemLabel = ({ selected, optionLabel }: ItemLabelProps) => {
  return (
    <Box className={styles.itemLabel}>
      <Box className={styles.itemLabelIcon}>
        {selected && <CheckIcon style={{ height: "16px", width: "16px" }} />}
      </Box>

      <Typography component="span" size="small">
        {optionLabel}
      </Typography>
    </Box>
  );
};
